import { loginSuccess, logoutUserSuccess, apiError, reset_login_flag } from './reducer';
import axios from 'axios';
import config from '../../../config';
import { setAuthorization } from '../../../helpers/api_helper';
import webSocketService from '../../../services/websocket.service';

export const loginUser = (user : any, history : any) => async (dispatch : any) => {
  try {
    // Determine if the input is an email or username
    const isEmail = user.email && user.email.includes('@');
    
    const response = await axios.post('/auth/login', {
      email: isEmail ? user.email : undefined,
      username: !isEmail ? user.email : undefined, // user.email contains the input from the form
      password: user.password
    });

    const { data } = response;

    if (data.success) {
      // Set default authorization header for future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      setAuthorization(data.token); // Also update through the helper function
      
      // Fetch user menu permissions if not admin
      let menuPermissions = null;
      if (data.user.level_id !== 1) {
        try {
          const menuPermsResponse = await axios.get(`${config.api.API_URL}/permissions/user-menu-permissions`);
          if (menuPermsResponse.data.success) {
            menuPermissions = menuPermsResponse.data.data.menu_items;
          }
        } catch (error) {
          console.error('Error fetching menu permissions:', error);
        }
      }
      
      // Store auth data in localStorage for persistence across tabs
      const authData = JSON.stringify({
        token: data.token,
        refreshToken: data.refreshToken,
        user: {
          ...data.user,
          menuPermissions: menuPermissions || []
        },
        data: {
          ...data.user,
          menuPermissions: menuPermissions || []
        }
      });
      localStorage.setItem("authUser", authData);
      // Also keep in sessionStorage for backwards compatibility
      sessionStorage.setItem("authUser", authData);
      
      dispatch(loginSuccess({
        ...data.user,
        menuPermissions: menuPermissions || []
      }));
      history('/base');
    } else {
      dispatch(apiError({ data: data.message }));
    }
  } catch (error : any) {
    const errorMessage = error.response?.data?.message || "Login failed";
    dispatch(apiError({ data: errorMessage }));
  }
};

export const logoutUser = () => async (dispatch : any) => {
  try {
    // Disconnect WebSocket before clearing auth
    webSocketService.disconnect();
    
    // Clear auth data from both storages
    localStorage.removeItem("authUser");
    sessionStorage.removeItem("authUser");
    delete axios.defaults.headers.common['Authorization'];
    
    // Clear any layout attributes to prevent errors during unmount
    if (document.documentElement) {
      document.documentElement.removeAttribute('data-layout');
      document.documentElement.removeAttribute('data-sidebar');
      document.documentElement.removeAttribute('data-sidebar-size');
      document.documentElement.removeAttribute('data-layout-mode');
      document.documentElement.removeAttribute('data-layout-width');
      document.documentElement.removeAttribute('data-layout-position');
      document.documentElement.removeAttribute('data-topbar');
      document.documentElement.removeAttribute('data-sidebar-image');
      document.documentElement.removeAttribute('data-preloader');
      document.documentElement.removeAttribute('data-theme');
      document.documentElement.removeAttribute('data-theme-colors');
    }
    
    // Optional: Call logout endpoint
    try {
      await axios.post('/auth/logout');
    } catch (error) {
      // Ignore logout API errors
    }
    
    dispatch(logoutUserSuccess(true));
  } catch (error) {
    dispatch(apiError(error));
  }
};

export const refreshTokenIfNeeded = () => async (dispatch : any) => {
  try {
    const authUser = localStorage.getItem("authUser") || sessionStorage.getItem("authUser");
    if (!authUser) {
      return false;
    }

    const { refreshToken } = JSON.parse(authUser);
    if (!refreshToken) {
      return false;
    }

    const response = await axios.post('/auth/refresh', { refreshToken });
    const { data } = response;

    if (data.success) {
      // Update stored tokens
      const existingAuth = JSON.parse(authUser);
      const updatedAuth = JSON.stringify({
        ...existingAuth,
        token: data.token,
        refreshToken: data.refreshToken,
        user: data.user
      });
      localStorage.setItem("authUser", updatedAuth);
      sessionStorage.setItem("authUser", updatedAuth);
      
      // Set new authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      setAuthorization(data.token);
      
      dispatch(loginSuccess(data.user));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

export const getCurrentUser = () => async (dispatch : any) => {
  try {
    const authUser = localStorage.getItem("authUser") || sessionStorage.getItem("authUser");
    if (!authUser) {
      return;
    }

    const { token } = JSON.parse(authUser);
    if (!token) {
      return;
    }

    // Set authorization header
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setAuthorization(token); // Also update through the helper function
    
    // Get current user data from API
    const response = await axios.get('/auth/me');
    const { data } = response;

    if (data.success) {
      // Fetch user menu permissions if not admin
      let menuPermissions = null;
      if (data.user.level_id !== 1) {
        try {
          const menuPermsResponse = await axios.get(`${config.api.API_URL}/permissions/user-menu-permissions`);
          if (menuPermsResponse.data.success) {
            menuPermissions = menuPermsResponse.data.data.menu_items;
          }
        } catch (error) {
          console.error('getCurrentUser - Error fetching menu permissions:', error);
        }
      }
      
      // Update both storages with menu permissions
      const existingAuth = JSON.parse(localStorage.getItem("authUser") || sessionStorage.getItem("authUser") || "{}");
      const updatedAuth = JSON.stringify({
        ...existingAuth,
        user: {
          ...data.user,
          menuPermissions: menuPermissions || []
        }
      });
      localStorage.setItem("authUser", updatedAuth);
      sessionStorage.setItem("authUser", updatedAuth);
      
      dispatch(loginSuccess({
        ...data.user,
        menuPermissions: menuPermissions || []
      }));
    } else {
      localStorage.removeItem("authUser");
      sessionStorage.removeItem("authUser");
      delete axios.defaults.headers.common['Authorization'];
    }
  } catch (error : any) {
    // Clear invalid auth data from both storages
    localStorage.removeItem("authUser");
    sessionStorage.removeItem("authUser");
    delete axios.defaults.headers.common['Authorization'];
  }
};

export const socialLogin = (type : any, history : any) => async (dispatch : any) => {
  try {
    // Social login not implemented yet
    dispatch(apiError({ data: "Social login not implemented" }));
  } catch (error) {
    dispatch(apiError(error));
  }
};

export const resetLoginFlag = () => async (dispatch : any) => {
  try {
    const response = dispatch(reset_login_flag());
    return response;
  } catch (error) {
    dispatch(apiError(error));
  }
};